import type { TrackReferenceOrPlaceholder } from '@livekit/components-react';
import { Track } from 'livekit-client';

export type GridPageResult = {
  tracksForPage: TrackReferenceOrPlaceholder[];
  totalPages: number;
  safePage: number;
  totalTracks: number;
  rangeStart: number;
  rangeEnd: number;
};

/**
 * Ordem estável: quem fala (debounced) → câmera ligada → demais (alfabético).
 * Aceita um Set de identities de oradores ativos (já com debounce aplicado no nível do componente)
 * em vez de Room diretamente, para evitar reordenações frequentes por flutuações de áudio.
 */
export function sortGridTracks(
  tracks: TrackReferenceOrPlaceholder[],
  activeSpeakerIds: Set<string>,
): TrackReferenceOrPlaceholder[] {
  const cameraTracks = tracks.filter((t) => t.source === Track.Source.Camera);
  const otherTracks = tracks.filter((t) => t.source !== Track.Source.Camera);

  const byMeetOrder = (a: TrackReferenceOrPlaceholder, b: TrackReferenceOrPlaceholder) => {
    const aId = a.participant.identity;
    const bId = b.participant.identity;
    const aActive = activeSpeakerIds.has(aId) ? 0 : 1;
    const bActive = activeSpeakerIds.has(bId) ? 0 : 1;
    if (aActive !== bActive) return aActive - bActive;

    const aVideo = a.participant.isCameraEnabled ? 0 : 1;
    const bVideo = b.participant.isCameraEnabled ? 0 : 1;
    if (aVideo !== bVideo) return aVideo - bVideo;

    const aName = (a.participant.name || aId).toLowerCase();
    const bName = (b.participant.name || bId).toLowerCase();
    return aName.localeCompare(bName, 'pt-BR');
  };

  return [...cameraTracks.sort(byMeetOrder), ...otherTracks];
}

/** Uma página do mosaico: `tilesPerPage` blocos por página, sem cortar a lista total. */
export function buildGridPage(
  tracks: TrackReferenceOrPlaceholder[],
  tilesPerPage: number,
  page: number,
): GridPageResult {
  const perPage = Math.max(1, Math.min(49, tilesPerPage));
  const totalTracks = tracks.length;
  const totalPages = Math.max(1, Math.ceil(totalTracks / perPage));
  const safePage = Math.min(Math.max(0, page), totalPages - 1);
  const start = safePage * perPage;
  const tracksForPage = tracks.slice(start, start + perPage);

  return {
    tracksForPage,
    totalPages,
    safePage,
    totalTracks,
    rangeStart: totalTracks === 0 ? 0 : start + 1,
    rangeEnd: start + tracksForPage.length,
  };
}

/**
 * Sugestão inicial apenas — não deve sobrescrever escolha manual do usuário.
 *
 * Em escritório/Wi‑Fi compartilhado, cada tile = decode + download de uma camada.
 * Com ~20 pessoas, 9 tiles simultâneos satura o downlink do cliente; preferimos
 * menos tiles + paginação (áudio de todos continua).
 */
export function suggestedMaxTiles(participantCount: number): number {
  if (participantCount <= 4) return 4;
  if (participantCount <= 10) return 9;
  if (participantCount <= 20) return 6;
  return 4;
}

export function shouldPreferCompactLayout(participantCount: number): boolean {
  return participantCount > 30;
}
